/**
 * Shared Bag Collection Interactive Filtering Controller
 * Breit Vacher Theme
 * Matches the exact behavior, markup, and styling of Breit Vacher's native collection filtering.
 */
(function() {
  const GROUP_LABELS = {
    color: 'Color',
    material: 'Material',
    closure: 'Closure',
    features: 'Features',
    storage_features: 'Storage features',
    carry: 'Carry options',
    strap_type: 'Bag strap type',
    accessory_size: 'Size',
    bag_type: 'Bag type',
    size: 'Size',
    style: 'Style',
    age_group: 'Age group',
    target_gender: 'Target gender',
  };

  const CLOSE_ICON_SVG = `
    <svg aria-hidden="true" focusable="false" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  let lastVisibleCount = 12;
  let totalProductCount = 12;
  let listenerController = null;
  let controllerGeneration = 0;
  let cachedCandidateSet = null;
  let candidateSetPromise = null;
  const FILTER_PARAM_PREFIX = 'filter.bag.';
  const LEGACY_FILTER_PARAM_PREFIX = 'filter.duffel.';
  const PRICE_MIN_PARAM = 'filter.v.price.gte';
  const PRICE_MAX_PARAM = 'filter.v.price.lte';

  function getBagColorSwatch(value) {
    const normalized = value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    if (normalized.includes('dark-brown') || normalized.includes('espresso')) return '#4b2e23';
    if (normalized.includes('brown') || normalized.includes('chocolate')) return '#795548';
    if (normalized.includes('black')) return '#111111';
    if (normalized.includes('orange')) return '#f28c28';
    if (normalized.includes('tan') || normalized.includes('camel') || normalized.includes('beige')) return '#c8a878';
    if (normalized.includes('red') || normalized.includes('burgundy')) return '#a52a2a';
    if (normalized.includes('blue') || normalized.includes('navy')) return '#315b8a';
    if (normalized.includes('green') || normalized.includes('olive')) return '#4b6043';
    if (normalized.includes('white') || normalized.includes('cream') || normalized.includes('ivory')) return '#ffffff';
    if (normalized.includes('gray') || normalized.includes('grey') || normalized.includes('silver')) return '#929292';
    return '#b8b8b8';
  }

  function initBagFilters() {
    if (!document.getElementById('product-grid')?.hasAttribute('data-bag-page-count')) return;

    if (document.querySelector('.facets-container[data-has-native-bag-filters="true"]')) return;

    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    const productCards = Array.from(productGrid.querySelectorAll('li[data-bag-product="true"]'));
    if (productCards.length === 0) return;
    listenerController?.abort();
    listenerController = new AbortController();
    const listenerSignal = listenerController.signal;
    const generation = ++controllerGeneration;
    const basePageMarkup = productCards.map(card => card.outerHTML);
    totalProductCount = Number(productGrid.dataset.bagTotalCount) || productCards.length;

    const desktopCountContainer = document.getElementById('ProductCountDesktop');
    const mobileCountContainer = document.getElementById('ProductCount');
    const desktopActiveFacets = Array.from(document.querySelectorAll('.active-facets.active-facets-desktop'));
    const mobileActiveFacets = Array.from(document.querySelectorAll('.active-facets.active-facets-mobile'));

    // Filter inputs
    let filterInputs = Array.from(document.querySelectorAll('input[data-filter-group]'));
    const minRange = document.querySelector('.price-slider-input.min-range');
    const maxRange = document.querySelector('.price-slider-input.max-range');
    const minPriceInput = document.querySelector('.price-field-min');
    const maxPriceInput = document.querySelector('.price-field-max');

    const absoluteMinPrice = minRange ? parseFloat(minRange.min || '0') : 0;
    const absoluteMaxPrice = maxRange ? parseFloat(maxRange.max || '350') : 350;
    const priceRangeStep = maxRange ? parseFloat(maxRange.step || '1') : 1;
    const effectiveMaxPrice = minRange && maxRange
      ? absoluteMinPrice + Math.floor((absoluteMaxPrice - absoluteMinPrice) / priceRangeStep) * priceRangeStep
      : absoluteMaxPrice;

    async function loadCompleteCandidateSet() {
      const sortBy = new URLSearchParams(window.location.search).get('sort_by') || '';
      const cacheKey = `${window.location.pathname}|${sortBy}`;
      if (cachedCandidateSet?.key === cacheKey) return cachedCandidateSet;
      if (candidateSetPromise?.key === cacheKey) return candidateSetPromise.promise;

      const sectionId = productGrid.dataset.id;
      if (!sectionId) {
        return {
          key: cacheKey,
          markup: basePageMarkup,
        totalCount: totalProductCount
        };
      }

      const fetchPage = async (page) => {
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('section_id', sectionId);
        url.searchParams.set('page', String(page));
        if (sortBy) url.searchParams.set('sort_by', sortBy);
        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Bag product page ${page} could not be loaded (${response.status}).`);
        const pageDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        const pageGrid = pageDocument.getElementById('product-grid');
        if (!pageGrid) throw new Error(`Bag product page ${page} did not contain a product grid.`);
        return pageGrid;
      };

      const promise = (async () => {
        const firstPage = await fetchPage(1);
        const pageCount = Math.max(1, Number(firstPage.dataset.bagPageCount) || 1);
        const allPageGrids = [firstPage];
        for (let start = 2; start <= pageCount; start += 4) {
          const pageNumbers = Array.from({ length: Math.min(4, pageCount - start + 1) }, (_, index) => start + index);
          allPageGrids.push(...await Promise.all(pageNumbers.map(fetchPage)));
        }

        const markup = allPageGrids.flatMap(pageGrid =>
          Array.from(pageGrid.querySelectorAll('li[data-bag-product="true"]'), card => card.outerHTML)
        );
        const totalCount = Number(firstPage.dataset.bagTotalCount) || markup.length;
        cachedCandidateSet = { key: cacheKey, markup, totalCount };
        return cachedCandidateSet;
      })();

      candidateSetPromise = { key: cacheKey, promise };
      try {
        return await promise;
      } finally {
        if (candidateSetPromise?.promise === promise) candidateSetPromise = null;
      }
    }

    function syncFacetOptionCounts(candidateMarkup) {
      const attributeByGroup = {
        color: 'data-colors',
        material: 'data-materials',
        closure: 'data-closures',
        features: 'data-features',
        storage_features: 'data-storage-features',
        carry: 'data-carry-options',
        strap_type: 'data-strap-types',
        accessory_size: 'data-accessory-sizes',
        bag_type: 'data-bag-types',
        size: 'data-sizes',
        style: 'data-styles',
        age_group: 'data-age-groups',
        target_gender: 'data-target-genders'
      };
      const countsByGroup = Object.fromEntries(Object.keys(attributeByGroup).map(group => [group, new Map()]));
      const template = document.createElement('template');

      candidateMarkup.forEach(markup => {
        template.innerHTML = markup;
        const card = template.content.firstElementChild;
        if (!card) return;
        Object.entries(attributeByGroup).forEach(([group, attribute]) => {
          const values = new Set((card.getAttribute(attribute) || '').split(',').map(value => value.trim()).filter(Boolean));
          values.forEach(value => {
            const counts = countsByGroup[group];
            counts.set(value, (counts.get(value) || 0) + 1);
          });
        });
      });

      document.querySelectorAll('details[data-index]').forEach(details => {
        const group = details.dataset.index.replace(/^mobile-/, '').replace(/^bag-/, '');
        const values = countsByGroup[group];
        const list = details.querySelector('.facets__list--vertical');
        if (!values || !list) return;
        if (values.size === 0) {
          details.remove();
          return;
        }

        const mobilePrefix = details.dataset.index.startsWith('mobile-') ? 'mobile-' : '';
        list.replaceChildren();
        Array.from(values.entries()).sort(([a], [b]) => a.localeCompare(b)).forEach(([value, count], index) => {
          const id = `Filter-${mobilePrefix}bag-${group}-${index + 1}`;
          const item = document.createElement('li');
          item.className = 'list-menu__item facets__item';
          const label = document.createElement('label');
          label.className = 'facets__label facet-checkbox';
          label.htmlFor = id;
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.name = `${FILTER_PARAM_PREFIX}${group}`;
          input.value = value;
          input.id = id;
          input.dataset.filterGroup = group;
          let visualControl;
          if (group === 'color') {
            label.classList.add('visual-display-parent', 'visual-display-parent--swatch');
            visualControl = document.createElement('div');
            visualControl.className = 'facets__visual-display-wrapper premium-swatch-wrapper';
            visualControl.dataset.color = value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            const swatch = document.createElement('span');
            swatch.className = 'visual-display visual-display--presentation-swatch';
            swatch.style.backgroundColor = getBagColorSwatch(value);
            visualControl.append(swatch);
          } else {
            visualControl = document.createElement('div');
            visualControl.className = 'facets__visual-checkmark-wrapper premium-checkmark-wrapper';
            const checkmarkDisplay = document.createElement('span');
            checkmarkDisplay.className = 'checkmark-display checkmark-display--presentation';
            visualControl.append(checkmarkDisplay);
          }
          const visibleText = document.createElement('span');
          visibleText.className = 'facet-checkbox__text';
          visibleText.setAttribute('aria-hidden', 'true');
          visibleText.append(document.createTextNode(`${value} `));
          const countText = document.createElement('span');
          countText.className = 'facet-count';
          countText.textContent = `(${count})`;
          visibleText.append(countText);
          const hiddenText = document.createElement('span');
          hiddenText.className = 'visually-hidden';
          hiddenText.textContent = `${value} (${count})`;
          label.append(input, visualControl, visibleText, hiddenText);
          item.append(label);
          list.append(item);
        });
      });
    }

    function restoreFiltersFromUrl() {
      const params = new URLSearchParams(window.location.search);

      filterInputs.forEach(input => {
        const group = input.getAttribute('data-filter-group');
        input.checked = [
          ...params.getAll(`${FILTER_PARAM_PREFIX}${group}`),
          ...params.getAll(`${LEGACY_FILTER_PARAM_PREFIX}${group}`)
        ].includes(input.value);
      });

      if (minRange && maxRange) {
        const minParam = params.get(PRICE_MIN_PARAM);
        const maxParam = params.get(PRICE_MAX_PARAM);
        const parsedMin = minParam === null ? NaN : Number(minParam);
        const parsedMax = maxParam === null ? NaN : Number(maxParam);
        if (Number.isFinite(parsedMin)) {
          minRange.value = String(Math.max(absoluteMinPrice, Math.min(absoluteMaxPrice, parsedMin)));
          if (minPriceInput) minPriceInput.value = minParam;
        }
        if (Number.isFinite(parsedMax)) {
          maxRange.value = String(Math.max(absoluteMinPrice, Math.min(absoluteMaxPrice, parsedMax)));
          if (maxPriceInput) maxPriceInput.value = maxParam;
        }
        updateSliderTrack();
      }
    }

    function syncUrlFromSelection(categories, minPrice, maxPrice, isPriceFiltered, historyMode = 'push') {
      const url = new URL(window.location.href);
      Object.keys(categories).forEach(group => url.searchParams.delete(`${FILTER_PARAM_PREFIX}${group}`));
      Object.keys(categories).forEach(group => url.searchParams.delete(`${LEGACY_FILTER_PARAM_PREFIX}${group}`));
      Object.keys(categories).forEach(group => {
        categories[group].forEach(value => url.searchParams.append(`${FILTER_PARAM_PREFIX}${group}`, value));
      });
      url.searchParams.delete('page');

      url.searchParams.delete(PRICE_MIN_PARAM);
      url.searchParams.delete(PRICE_MAX_PARAM);
      if (isPriceFiltered) {
        url.searchParams.set(PRICE_MIN_PARAM, String(minPrice));
        url.searchParams.set(PRICE_MAX_PARAM, String(maxPrice));
      }

      if (url.href !== window.location.href) {
        window.history[`${historyMode}State`]({}, '', url);
      }
    }

    function syncActiveInputStyles() {
      filterInputs.forEach(input => {
        const label = input.closest('.facet-checkbox');
        if (label) label.classList.toggle('active', input.checked);
      });
    }

    // Currency symbol
    const currencyElem = document.querySelector('.field-currency');
    const currencySymbol = currencyElem ? currencyElem.textContent.trim() : '$';

    function getSelectedFilters() {
      const selected = {
        color: [],
        material: [],
        closure: [],
        features: [],
        storage_features: [],
        carry: [],
        strap_type: [],
        accessory_size: [],
        bag_type: [],
        size: [],
        style: [],
        age_group: [],
        target_gender: []
      };

      filterInputs.forEach(input => {
        if (input.checked) {
          const group = input.getAttribute('data-filter-group');
          const val = input.value;
          if (selected[group] && !selected[group].includes(val)) {
            selected[group].push(val);
          }
        }
      });

      let minP = minRange ? parseFloat(minRange.value) : absoluteMinPrice;
      let maxP = maxRange ? parseFloat(maxRange.value) : absoluteMaxPrice;

      if (minPriceInput && minPriceInput.value !== '') {
        const parsed = parseFloat(minPriceInput.value);
        if (!isNaN(parsed)) minP = Math.max(minP, parsed);
      }
      if (maxPriceInput && maxPriceInput.value !== '') {
        const parsed = parseFloat(maxPriceInput.value);
        if (!isNaN(parsed)) maxP = Math.min(maxP, parsed);
      }
      if (maxP >= effectiveMaxPrice - 0.01) maxP = absoluteMaxPrice;

      const isPriceFiltered = (minP > absoluteMinPrice + 0.5) || (maxP < absoluteMaxPrice - 0.5);

      return {
        categories: selected,
        minPrice: minP,
        maxPrice: maxP,
        isPriceFiltered: isPriceFiltered
      };
    }

    function updateProductCount(visibleCount, totalCount) {
      lastVisibleCount = visibleCount;
      let countText = '';
      if (visibleCount === totalCount) {
        countText = totalCount === 1 ? '1 product' : `${totalCount} products`;
      } else {
        countText = `${visibleCount} of ${totalCount} products`;
      }

      if (desktopCountContainer) {
        desktopCountContainer.textContent = countText;
        desktopCountContainer.classList.remove('loading');
      }
      if (mobileCountContainer) {
        mobileCountContainer.textContent = countText;
        mobileCountContainer.classList.remove('loading');
      }
    }

    function updateSliderTrack() {
      const trackHighlight = document.querySelector('.price-slider-track-highlight');
      if (trackHighlight && minRange && maxRange) {
        const minVal = parseFloat(minRange.value);
        const maxVal = parseFloat(maxRange.value);
        const maxPrice = parseFloat(maxRange.max || '309');
        const minPercent = (minVal / maxPrice) * 100;
        const maxPercent = (maxVal / maxPrice) * 100;
        trackHighlight.style.left = minPercent + '%';
        trackHighlight.style.width = (maxPercent - minPercent) + '%';
      }
    }

    function clearAllFilters() {
      filterInputs.forEach(inp => {
        inp.checked = false;
      });

      if (minRange && maxRange) {
        minRange.value = absoluteMinPrice;
        maxRange.value = absoluteMaxPrice;
        if (minPriceInput) minPriceInput.value = '';
        if (maxPriceInput) maxPriceInput.value = '';
        updateSliderTrack();
      }

      applyFilters();
    }

    function removeCategoryFilter(group, val) {
      const matchingInputs = document.querySelectorAll(`input[data-filter-group="${group}"][value="${val}"]`);
      matchingInputs.forEach(inp => {
        inp.checked = false;
      });
      applyFilters();
    }

    function removePriceFilter() {
      if (minRange && maxRange) {
        minRange.value = absoluteMinPrice;
        maxRange.value = absoluteMaxPrice;
        if (minPriceInput) minPriceInput.value = '';
        if (maxPriceInput) maxPriceInput.value = '';
        updateSliderTrack();
      }
      applyFilters();
    }

    function renderActiveFacets(categories, isPriceFiltered, minPrice, maxPrice) {
      const activeItems = [];
      Object.keys(categories).forEach(group => {
        categories[group].forEach(val => {
          activeItems.push({
            group: group,
            label: GROUP_LABELS[group] || group,
            val: val
          });
        });
      });

      const hasActive = activeItems.length > 0 || isPriceFiltered;

      // 1. Desktop Active Facets
      desktopActiveFacets.forEach(container => {
        if (!hasActive) {
          container.innerHTML = '';
        } else {
          let desktopHtml = `
            <div class="active-facets__header">
              <span class="facets__filter-title">Filtered by</span>
              <facet-remove class="active-facets__clear-all">
                <a href="javascript:void(0)" class="active-facets__button-remove duffel-clear-all-link">
                  <span>Clear all</span>
                </a>
              </facet-remove>
            </div>
          `;

          activeItems.forEach(item => {
            desktopHtml += `
              <facet-remove class="active-facets__remove">
                <a href="javascript:void(0)" class="active-facets__button active-facets__button--light duffel-remove-facet" data-group="${item.group}" data-val="${item.val}">
                  <span class="active-facets__button-inner button button--tertiary">
                    ${item.label}: ${item.val}
                    ${CLOSE_ICON_SVG}
                    <span class="visually-hidden">Remove filter</span>
                  </span>
                </a>
              </facet-remove>
            `;
          });

          if (isPriceFiltered) {
            desktopHtml += `
              <facet-remove class="active-facets__remove">
                <a href="javascript:void(0)" class="active-facets__button active-facets__button--light duffel-remove-price">
                  <span class="active-facets__button-inner button button--tertiary">
                    <span class="active-facets__price-wrapper">
                      ${currencySymbol}${minPrice.toFixed(2)} - ${currencySymbol}${maxPrice.toFixed(2)}
                    </span>
                    ${CLOSE_ICON_SVG}
                    <span class="visually-hidden">Remove filter</span>
                  </span>
                </a>
              </facet-remove>
            `;
          }

          container.innerHTML = desktopHtml;

          // Desktop Event Handlers
          container.querySelectorAll('.duffel-remove-facet').forEach(link => {
            link.addEventListener('click', function(e) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              removeCategoryFilter(this.getAttribute('data-group'), this.getAttribute('data-val'));
            });
          });

          const priceRemove = container.querySelector('.duffel-remove-price');
          if (priceRemove) {
            priceRemove.addEventListener('click', function(e) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              removePriceFilter();
            });
          }

          const clearAllBtn = container.querySelector('.duffel-clear-all-link');
          if (clearAllBtn) {
            clearAllBtn.addEventListener('click', function(e) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              clearAllFilters();
            });
          }
        }
      });

      // 2. Mobile Active Facets
      mobileActiveFacets.forEach(container => {
        if (!hasActive) {
          container.innerHTML = '';
        } else {
          let mobileHtml = `
            <div class="facets__filter-title-container">
              <span class="facets__filter-title">Filtered by</span>
            </div>
          `;

          activeItems.forEach(item => {
            mobileHtml += `
              <facet-remove class="remove-container">
                <a href="javascript:void(0)" class="active-facets__button active-facets__button--light duffel-remove-facet" data-group="${item.group}" data-val="${item.val}">
                  <span class="active-facets__button-inner button button--tertiary">
                    ${item.label}: ${item.val}
                    ${CLOSE_ICON_SVG}
                    <span class="visually-hidden">Remove filter</span>
                  </span>
                </a>
              </facet-remove>
            `;
          });

          if (isPriceFiltered) {
            mobileHtml += `
              <facet-remove class="remove-container">
                <a href="javascript:void(0)" class="active-facets__button active-facets__button--light duffel-remove-price">
                  <span class="active-facets__button-inner button button--tertiary">
                    <span class="active-facets__price-wrapper">
                      ${currencySymbol}${minPrice.toFixed(2)} - ${currencySymbol}${maxPrice.toFixed(2)}
                    </span>
                    ${CLOSE_ICON_SVG}
                    <span class="visually-hidden">Remove filter</span>
                  </span>
                </a>
              </facet-remove>
            `;
          }

          container.innerHTML = mobileHtml;

          // Mobile Event Handlers
          container.querySelectorAll('.duffel-remove-facet').forEach(link => {
            link.addEventListener('click', function(e) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              removeCategoryFilter(this.getAttribute('data-group'), this.getAttribute('data-val'));
            });
          });

          const mPriceRemove = container.querySelector('.duffel-remove-price');
          if (mPriceRemove) {
            mPriceRemove.addEventListener('click', function(e) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              removePriceFilter();
            });
          }
        }
      });
    }

    async function applyFilters({ updateUrl = true, historyMode = 'push' } = {}) {
      const { categories, minPrice, maxPrice, isPriceFiltered } = getSelectedFilters();
      const hasSelectedFilters = isPriceFiltered || Object.values(categories).some(values => values.length > 0);
      syncActiveInputStyles();
      if (updateUrl) syncUrlFromSelection(categories, minPrice, maxPrice, isPriceFiltered, historyMode);
      let candidateMarkup = basePageMarkup;
      if (hasSelectedFilters) {
        try {
          const candidateSet = await loadCompleteCandidateSet();
          if (generation !== controllerGeneration) return;
          candidateMarkup = candidateSet.markup;
          totalProductCount = candidateSet.totalCount;
          syncFacetOptionCounts(candidateMarkup);
          // Rebuilding each facet list replaces its inputs, so bind the new controls
          // and restore the selected values before the next user interaction.
          bindFilterInputs();
          restoreFiltersFromUrl();
          syncActiveInputStyles();
        } catch (error) {
          console.error('[Bag filters] Could not load all collection products for filtering.', error);
          return;
        }
      }

      if (generation !== controllerGeneration) return;
      const currentProductGrid = document.getElementById('product-grid');
      if (!currentProductGrid) return;
      const template = document.createElement('template');
      let visibleCount = 0;
      const visibleMarkup = [];

      candidateMarkup.forEach(markup => {
        template.innerHTML = markup;
        const card = template.content.firstElementChild;
        if (!card) return;

        const price = parseFloat(card.getAttribute('data-product-price') || '0');
        const colors = (card.getAttribute('data-colors') || '').split(',').map(s => s.trim()).filter(Boolean);
        const materials = (card.getAttribute('data-materials') || '').split(',').map(s => s.trim()).filter(Boolean);
        const closures = (card.getAttribute('data-closures') || '').split(',').map(s => s.trim()).filter(Boolean);
        const carryOpts = (card.getAttribute('data-carry-options') || '').split(',').map(s => s.trim()).filter(Boolean);
        const features = (card.getAttribute('data-features') || '').split(',').map(s => s.trim()).filter(Boolean);
        const storageFeatures = (card.getAttribute('data-storage-features') || '').split(',').map(s => s.trim()).filter(Boolean);
        const strapTypes = (card.getAttribute('data-strap-types') || '').split(',').map(s => s.trim()).filter(Boolean);
        const accessorySizes = (card.getAttribute('data-accessory-sizes') || '').split(',').map(s => s.trim()).filter(Boolean);
        const bagTypes = (card.getAttribute('data-bag-types') || '').split(',').map(s => s.trim()).filter(Boolean);
        const sizes = (card.getAttribute('data-sizes') || '').split(',').map(s => s.trim()).filter(Boolean);
        const styles = (card.getAttribute('data-styles') || '').split(',').map(s => s.trim()).filter(Boolean);
        const ageGroups = (card.getAttribute('data-age-groups') || '').split(',').map(s => s.trim()).filter(Boolean);
        const targetGenders = (card.getAttribute('data-target-genders') || '').split(',').map(s => s.trim()).filter(Boolean);

        function matchesGroup(selectedVals, productVals) {
          if (!selectedVals || selectedVals.length === 0) return true;
          return selectedVals.some(value => productVals.includes(value));
        }

        const matchesPrice = !isPriceFiltered || (price >= minPrice && price <= maxPrice);
        const matchesFilters =
          matchesGroup(categories.color, colors) &&
          matchesGroup(categories.material, materials) &&
          matchesGroup(categories.closure, closures) &&
          matchesGroup(categories.carry, carryOpts) &&
          matchesGroup(categories.features, features) &&
          matchesGroup(categories.storage_features, storageFeatures) &&
          matchesGroup(categories.strap_type, strapTypes) &&
          matchesGroup(categories.accessory_size, accessorySizes) &&
          matchesGroup(categories.bag_type, bagTypes) &&
          matchesGroup(categories.size, sizes) &&
          matchesGroup(categories.style, styles) &&
          matchesGroup(categories.age_group, ageGroups) &&
          matchesGroup(categories.target_gender, targetGenders);

        if (matchesPrice && matchesFilters) {
          visibleMarkup.push(markup);
          visibleCount++;
        }
      });

      const cardsToRender = hasSelectedFilters ? visibleMarkup : basePageMarkup;
      currentProductGrid.replaceChildren(...cardsToRender.map(markup => {
        template.innerHTML = markup;
        return template.content.firstElementChild;
      }).filter(Boolean));
      const pagination = currentProductGrid.closest('.collection')?.querySelector('.pagination-wrapper');
      if (pagination) pagination.style.display = hasSelectedFilters ? 'none' : '';

      // Update product count label
      updateProductCount(hasSelectedFilters ? visibleCount : totalProductCount, totalProductCount);

      // Handle empty state
      let emptyMsg = document.getElementById('bag-empty-filters-msg');
      if (visibleCount === 0) {
        if (!emptyMsg) {
          emptyMsg = document.createElement('div');
          emptyMsg.id = 'bag-empty-filters-msg';
          emptyMsg.className = 'collection collection--empty';
          emptyMsg.style.cssText = 'grid-column: 1 / -1; padding: 4rem 1rem; text-align: center; width: 100%;';
          emptyMsg.innerHTML = `
            <h3 class="title title--primary" style="font-size: 1.6rem; margin-bottom: 1.5rem;">No bags match your selected filters.</h3>
            <button type="button" class="button button--secondary bag-clear-btn" style="padding: 1rem 2rem; cursor: pointer;">Clear all filters</button>
          `;
          currentProductGrid.after(emptyMsg);
          emptyMsg.querySelector('.bag-clear-btn').addEventListener('click', clearAllFilters, { signal: listenerSignal });
        }
        emptyMsg.style.display = 'block';
      } else if (emptyMsg) {
        emptyMsg.remove();
      }

      // Render Active Facets at the top
      renderActiveFacets(categories, isPriceFiltered, minPrice, maxPrice);
    }

    function bindFilterInputs() {
      filterInputs = Array.from(document.querySelectorAll('input[data-filter-group]'));
      filterInputs.forEach(input => {
        input.addEventListener('change', function(e) {
          e.stopPropagation();
          e.stopImmediatePropagation();
          const sameInputs = document.querySelectorAll(`input[data-filter-group="${this.dataset.filterGroup}"]`);
          sameInputs.forEach(sameInput => {
            if (sameInput.value === this.value) sameInput.checked = this.checked;
          });
          applyFilters();
        }, { signal: listenerSignal });
      });
    }

    // Attach listeners to price slider inputs
    [minRange, maxRange].forEach(elem => {
      if (elem) {
        elem.addEventListener('input', function(e) {
          e.stopPropagation();
          e.stopImmediatePropagation();
          applyFilters({ historyMode: 'replace' });
        }, { signal: listenerSignal });
      }
    });

    [minPriceInput, maxPriceInput].forEach(elem => {
      if (elem) {
        elem.addEventListener('change', function(e) {
          e.stopPropagation();
          e.stopImmediatePropagation();
          applyFilters({ historyMode: 'replace' });
        }, { signal: listenerSignal });
      }
    });

    // Mobile drawer clear button listener
    document.querySelectorAll('.mobile-facets__clear').forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        clearAllFilters();
      }, { signal: listenerSignal });
    });

    // Expose global interface
    window.BagFilter = {
      applyFilters: applyFilters,
      updateCount: () => updateProductCount(lastVisibleCount, totalProductCount),
      refresh: initBagFilters,
      clearAll: clearAllFilters
    };

    // Load every collection page first so each facet shows complete values and counts.
    async function initializeBagFilters() {
      try {
        const candidateSet = await loadCompleteCandidateSet();
        if (generation !== controllerGeneration) return;
        totalProductCount = candidateSet.totalCount;
        syncFacetOptionCounts(candidateSet.markup);
      } catch (error) {
        console.error('[Bag filters] Could not load all collection products for filter options.', error);
      }
      if (generation !== controllerGeneration) return;
      bindFilterInputs();
      restoreFiltersFromUrl();
      applyFilters({ updateUrl: false });
    }

    initializeBagFilters();

    window.addEventListener('popstate', () => {
      restoreFiltersFromUrl();
      applyFilters({ updateUrl: false });
    }, { signal: listenerSignal });
  }

  if (document.readyState !== 'loading') {
    initBagFilters();
  } else {
    document.addEventListener('DOMContentLoaded', initBagFilters);
  }

  document.addEventListener('shopify:section:load', initBagFilters);
})();
